<template>
  <div class="document-scanner">
    <toolbar
      backRoute="/dashboard"
      title="Document Scanner"
      :addButton="false" />

    <div v-if="currentMode === 'list'">
      <vue-dropzone
        ref="vueDropzone"
        id="dropzone"
        :options="dropZoneConfig"
        @vdropzone-sending="handleSending"
        @vdropzone-queue-complete="handleQueueComplete"
        @vdropzone-success="handleSuccess"
        @vdropzone-error="handleError">
      </vue-dropzone>

      <div class="position-relative">
        <div
          v-if="isUploading"
          class="pb-3 pt-3 text-center overlay-loading"
          style="z-index: 1; border-radius: 0;">
          Processing images&hellip;
        </div>

        <div
          v-if="pendingMode !== null"
          class="pb-3 pt-3 text-center overlay-loading"
          style="z-index: 1; border-radius: 0;">
          Loading necessary data&hellip;
        </div>

        <d-alert
          v-if="loadError"
          theme="danger"
          class="text-center mt-3"
          show>
          Failed to load required data. Please try again.
        </d-alert>

        <d-alert
          v-if="uploadHistory.length === 0"
          theme="info"
          class="text-center mt-5"
          show>
          No images uploaded yet.
        </d-alert>

        <div
          v-else
          class="mt-5">
          <div class="d-flex flex-wrap align-items-center">

            <d-button
              theme="danger"
              :disabled="!uploadHistory.length || isUploading || isExporting || !selectedIds.length"
              class="mr-auto"
              @click.prevent="removeSelected">
              <i class="material-icons">delete</i>
              Remove selected ({{ selectedIds.length }})
            </d-button>

            <d-button
              theme="primary"
              class="mr-2"
              :disabled="!uploadHistory.length || isUploading || isExporting"
              @click.prevent="exportToCSV">
              <i class="material-icons">download</i>
              Export <span v-if="selectedIds.length"> selected ({{ selectedIds.length }}) </span>to CSV
            </d-button>

            <d-button
              theme="primary"
              class="mr-4"
              :disabled="!uploadHistory.length || isUploading || isExporting"
              @click.prevent="exportToExcel">
              <i class="material-icons">download</i>
              Export <span v-if="selectedIds.length"> selected ({{ selectedIds.length }}) </span>to XLSX
            </d-button>

            <d-button
              theme="success"
              class="mr-2"
              :disabled="!selectedIds.length || pendingMode !== null"
              @click.prevent="handleActionClick('import')">
              <i class="material-icons">person_add</i>
              Import <span v-if="selectedIds.length"> selected ({{ selectedIds.length }}) </span>
            </d-button>

            <d-button
              theme="warning"
              :disabled="!selectedIds.length || pendingMode !== null"
              @click.prevent="handleActionClick('update')">
              <i class="material-icons">update</i>
              Update <span v-if="selectedIds.length"> selected ({{ selectedIds.length }}) </span>
            </d-button>

          </div>

          <div class="table-wrapper">
            <table class="table table-striped table-bordered table-hover mt-3">
              <thead>
                <tr>
                  <th class="is-sticky align-middle">
                    <d-checkbox
                      key="checkbox-select-all"
                      v-model="allSelected"
                      @change="toggleSelectAll">
                    </d-checkbox>
                  </th>
                  <th>Image</th>
                  <th
                    v-for="colName in tableColumns"
                    :key="'col-head-' + colName.replace(' ', '-')">
                    {{ colName }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="(image, index) of uploadHistory"
                  :key="'image-' + index">
                  <td class="is-sticky align-middle">
                    <d-checkbox
                      :value="image.id"
                      v-model="selectedIds">
                    </d-checkbox>
                  </td>
                  <td class="align-middle">
                    <img
                      :src="image.thumbnail"
                      class="image-thumbnail" />
                    <small
                      class="d-block mt-2"
                      style="line-height: 1;">{{ image.fileName }}</small>
                  </td>
                  <td
                    v-if="image.status === 'error'"
                    :colspan="tableColumns.length ? tableColumns.length : 1"
                    class="text-danger">
                    {{ image.errorMessage || 'File analysis failed' }}
                  </td>
                  <template v-else>
                    <td
                      v-for="colName in tableColumns"
                      :key="'col-body-' + colName.replace(' ', '-')">
                      {{ (image.data && image.data[colName]) ? image.data[colName] : '' }}
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <pax-import
      v-else-if="currentMode === 'import'"
      :countriesOptions="countriesOptions"
      :operators="operators"
      :pax-data="selectedItemsData"
      @cancel="currentMode = 'list'" />

    <pax-update
      v-else-if="currentMode === 'update'"
      :countriesOptions="countriesOptions"
      :operators="operators"
      :pax-data="selectedItemsData"
      @cancel="currentMode = 'list'" />

  </div>
</template>

<script>
import Vue from 'vue';
import SanitizeUtils from '@/utils/SanitizeUtils';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import ExcelJS from 'exceljs';
import CommonGetters from '@/utils/CommonGetters.js';
import FormUtils from '@/utils/FormUtils.js';
import PaxImport from '@/components/form-parts/PaxImport.vue';
import PaxUpdate from '@/components/form-parts/PaxUpdate.vue';

export default {
  name: 'document-scanner',
  components: {
    'vue-dropzone': vue2Dropzone,
    'pax-import': PaxImport,
    'pax-update': PaxUpdate
  },
  computed: {
    additionalDataLoaded () {
      return this.loadedCountries && this.loadedOperators;
    },
    tableColumns () {
      let successFullItems = this.uploadHistory.filter(item => item.status === 'success');
      return [...new Set(successFullItems.flatMap(item => Object.keys(item.data)))];
    },
    selectedItemsData () {
      let filteredItems = this.uploadHistory.filter(item => this.selectedIds.indexOf(item.id) >= 0);

      if (!filteredItems.length) {
        return filteredItems;
      }

      return filteredItems.map(item => {
        let country = item.data['Country of issue'] || item.data['Country of Birth'];
        let countryIds = [];

        if (country) {
          let countryFound = this.countriesOptions.find(countryOpt => countryOpt.text.toLowerCase() === country.toLowerCase());
          if (countryFound) {
            countryIds.push(countryFound.id);
          }
        }

        let mappedSex = 'M';
        let rawGender = item.data['Gender'];

        if (rawGender) {
          let normalizedGender = rawGender.trim().toLowerCase();
          if (normalizedGender === 'female' || normalizedGender === 'f') {
            mappedSex = 'F';
          } else if (normalizedGender === 'male' || normalizedGender === 'm') {
            mappedSex = 'M';
          }
        }

        let nationalityId = null;
        if (item.data['Nationality']) {
          let found = this.countriesOptions.find(c =>
            c.text.toLowerCase() === item.data['Nationality'].toLowerCase() ||
            c.iso_a3 === item.data['Nationality']);
          if (found) {
            nationalityId = found.id;
          }
        }

        return {
          id: item.id, // this is used on front only, ID of processed doc
          passenger: {
            first_name: item.data['First Name'] || '',
            last_name: item.data['Last Name'] || '',
            birth_date: this.parseDocDateStringToObject(item.data['Date of Birth']),
            birth_place: item.data['Place of Birth'] || '',
            sex: mappedSex
          },
          passport: {
            name: '',
            serial_number: item.data['ID Number'] || '',
            country_id: nationalityId,
            countries: countryIds,
            expiry: this.parseDocDateStringToObject(item.data['ID expiry date']),
            is_default: 0
          },
          customer_id: ''
        };
      });
    },
    countriesOptions () {
      return CommonGetters.getCountriesForSelect(this.countries);
    },
  },
  watch: {
    selectedIds (newVal) {
      if (newVal.length === this.uploadHistory.length && this.uploadHistory.length > 0) {
        this.allSelected = true;
        return;
      }

      this.allSelected = false;
    },
    additionalDataLoaded (newVal) {
      if (newVal === true && this.pendingMode !== null) {
        this.currentMode = this.pendingMode;
        this.pendingMode = null;
      }
    },
    loadError (newVal) {
      if (newVal === true && this.pendingMode !== null) {
        this.pendingMode = null;
      }
    }
  },
  data () {
    return {
      additionalCsvColumns: [
        'Email', 'Visa number',
        'Visa Country of issue',
        'Visa expiry date',
        'Weight',
        'Unit'],
      allSelected: false,
      countries: [],
      currentMode: 'list', // 'list', 'import', 'update'
      dropZoneConfig: {
        acceptedFiles: 'image/png, image/jpg, image/jpeg',
        dictDefaultMessage: 'Drag images here',
        headers: {
          'Authorization': 'Bearer ' + SanitizeUtils.getToken()
        },
        parallelUploads: 1,
        paramName: 'image',
        thumbnailHeight: 150,
        thumbnailWidth: 150,
        url: this.$http.defaults.baseURL + '/image-processing/store'
      },
      isExporting: false,
      isUploading: false,
      loadedCountries: false,
      loadedOperators: false,
      loadError: false,
      operators: [],
      pendingMode: null,
      selectedIds: [],
      uploadHistory: []
    };
  },
  mounted () {
  },
  methods: {
    handleActionClick (modeName) {
      if (this.additionalDataLoaded) {
        this.currentMode = modeName;
      } else {
        this.pendingMode = modeName;
        this.loadError = false;

        this.loadAdditionalData();
      }
    },
    handleSending () {
      this.isUploading = true;
    },
    handleQueueComplete () {
      this.isUploading = false;
    },
    handleSuccess (file, response) {
      if (!response.success || (Array.isArray(response.data) && !response.data.length)) {
        this.handleError(file, 'Server returned success, but data is empty or invalid.');
        return;
      }
      this.uploadHistory.push({
        id: file.upload.uuid,
        fileName: file.name,
        status: 'success',
        data: response.data,
        errorMessage: null,
        thumbnail: file.dataURL
      });
    },
    handleError (file, message = null) {
      this.uploadHistory.push({
        id: file.upload.uuid,
        fileName: file.name,
        status: 'error',
        data: null,
        errorMessage: (message && message.message) ? message.message : (message || 'Processing failed'),
        thumbnail: file.dataURL
      });
    },
    prepareExportData () {
      const headers = [...this.tableColumns, ...this.additionalCsvColumns];
      let rowsToExport = [];

      if (this.selectedIds.length && !this.allSelected) {
        rowsToExport = this.uploadHistory.filter(item => this.selectedIds.includes(item.id));
      } else {
        rowsToExport = [...this.uploadHistory];
      }

      return { headers, rowsToExport };
    },
    downloadBlob (blob, fileName) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    escapeCsvValue (value) {
      if (value === null || value === undefined) {
        return '';
      }
      let stringVal = String(value);
      if (stringVal.includes(',') || stringVal.includes('\n') || stringVal.includes('"')) {
        return `"${stringVal.replace(/"/g, '""')}"`;
      }
      return stringVal;
    },
    exportToCSV () {
      this.isExporting = true;
      const { headers, rowsToExport } = this.prepareExportData();

      const csvRows = rowsToExport.map(item => {
        if (item.status === 'success') {
          let fieldValues = this.tableColumns.map(key => (item.data && item.data[key]) ? item.data[key] : '');
          let emptyExtras = new Array(this.additionalCsvColumns.length).fill('');
          return [...fieldValues, ...emptyExtras];
        }

        let errorRow = [`Error: ${item.errorMessage || 'Failed'}`];
        let remainingSlots = headers.length - errorRow.length;

        if (remainingSlots > 0) {
          let emptySlots = new Array(remainingSlots).fill('');
          errorRow.push(...emptySlots);
        }
        return errorRow;
      });

      let csvContent = [
        headers.map(this.escapeCsvValue).join(','),
        ...csvRows.map(row => row.map(this.escapeCsvValue).join(','))
      ].join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      this.downloadBlob(blob, `documents_analysis_${new Date().toISOString().slice(0, 10)}.csv`);
      this.isExporting = false;
    },
    exportToExcel () {
      this.isExporting = true;
      let { headers, rowsToExport } = this.prepareExportData();

      let workbook = new ExcelJS.Workbook();
      let worksheet = workbook.addWorksheet('Document Analysis');

      worksheet.columns = headers.map(col => ({ header: col, key: col, width: 20 }));

      rowsToExport.forEach(item => {
        if (item.status === 'success') {
          worksheet.addRow(item.data);
        } else {
          const errorData = { [headers]: `Error: ${item.errorMessage || 'Failed'}` };
          worksheet.addRow(errorData);
        }
      });

      worksheet.getRow(1).font = { bold: true };

      workbook.xlsx.writeBuffer()
        .then(buffer => {
          const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          this.downloadBlob(blob, `documents_analysis_${new Date().toISOString().slice(0, 10)}.xlsx`);
        })
        .catch(err => console.error('Excel export failed:', err))
        .finally(() => {
          this.isExporting = false;
        });
    },
    toggleSelectAll (newVal) {
      if (newVal) {
        Vue.set(this, 'selectedIds', this.uploadHistory.map(item => item.id));
        return;
      }
      Vue.set(this, 'selectedIds', []);
    },
    removeSelected () {
      this.uploadHistory = this.uploadHistory.filter(item => !this.selectedIds.includes(item.id));
      this.selectedIds = [];
    },
    parseDocDateStringToObject (dateString) {
      if (!dateString || !dateString.length) {
        return null;
      }

      const parts = dateString.split('.');
      let [day, month, year] = dateString.split('.');

      if (parts.length === 3) {
        day = parseInt(day, 10);
        month = parseInt(month, 10) - 1;
        year = parseInt(year, 10);

        return new Date(year, month, day);
      }

      return null;
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/countries/items',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/items',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedOperators',
        outputKey: 'operators',
        errorKey: 'loadError'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.table-wrapper {
  overflow-x: auto;
  width: 100%;
  position: relative;
}
.image-thumbnail {
  max-width: 100px;
  max-height: 100px;
}

.is-sticky {
  position: sticky;
  left: 0;
  z-index: 2;
}

th.is-sticky,
td.is-sticky {
  background-color: #fff;

  &:after,
  &:before {
    background-color: #dee2e6;
    content: "";
    height: 100%;
    position: absolute;
    top: 0;
    width: 2px;
  }

  &:before {
    left: -1px;
  }
  &:after {
    right: 0;
  }
}

.table-striped tbody tr:nth-of-type(odd) td.is-sticky {
  background-color: #f4f4f4;
}

.table thead th:not(.is-sticky) {
  min-width: 150px;
}

.table thead th.is-sticky {
  min-width: 50px;
}

.dbl-click-cell {
  cursor: pointer;
  border-bottom: 1px dashed #007bff;
  display: block;
  min-height: 24px;
  padding: 2px;
  transition: background-color 0.2s;
}

.dbl-click-cell:hover {
  background-color: #e9ecef;
}
</style>
